import React, { useState } from 'react';
import { SearchIcon, ShieldCheckIcon, AlertTriangleIcon, InfoIcon, XCircleIcon, ChevronDownIcon, ChevronRightIcon, FilterIcon, FileTextIcon, FolderIcon, CodeIcon, SlidersIcon, ExternalLinkIcon, CheckCircleIcon } from 'lucide-react';
import { DEFAULT_POLICY } from '../../../governance/pipeline/defaultPolicy';
// ─── Types ────────────────────────────────────────────────────────────────────
type Severity = 'BLOCKER' | 'WARNING' | 'INFO';
type Stage = 'schema' | 'type' | 'content' | 'support' | 'visual' | 'graph' | 'policy';
interface Rule {
  id: string;
  label: string;
  description: string;
  severity: Severity;
  stage: Stage;
  category: string;
  example?: string;
  fix?: string;
}
type ViewId = 'regels' | 'regelboeken' | 'drempelwaarden';
// ─── Static rule definitions ──────────────────────────────────────────────────
const RULES: Rule[] = [
// Schema Stage
{
  id: 'SCH-001',
  label: 'Verplichte velden aanwezig',
  description: 'Elke vraag moet de velden id, familyId, module, skill, subSkill, tier, levelCode, group, question, options, correctAnswer en feedback bevatten.',
  severity: 'BLOCKER',
  stage: 'schema',
  category: 'Structuur',
  example: 'Zie: src/governance/pipeline/stages/schemaStage.ts',
  fix: 'Voeg de ontbrekende verplichte velden toe aan de vraagdefinitie.'
}, {
  id: 'SCH-002',
  label: 'Uniek vraag-ID',
  description: 'Elk vraag-ID moet uniek zijn binnen het volledige systeem.',
  severity: 'BLOCKER',
  stage: 'schema',
  category: 'Structuur',
  fix: 'Gebruik het naamgevingsschema: [FAMILIE]-[NUMMER] (bijv. GET-BREUK-BASIS-001).'
}, {
  id: 'SCH-003',
  label: 'Geldige tier (1–16)',
  description: 'Tier moet een getal zijn tussen 1 en 16.',
  severity: 'BLOCKER',
  stage: 'schema',
  category: 'Metadata',
  fix: 'Pas de tier aan naar een waarde tussen 1 en 16.'
}, {
  id: 'SCH-004',
  label: 'Geldig groepsjaar (6, 7 of 8)',
  description: 'Het veld "group" moet 6, 7 of 8 zijn.',
  severity: 'BLOCKER',
  stage: 'schema',
  category: 'Metadata',
  fix: 'Stel group in op 6, 7 of 8.'
},
// Type Stage
{
  id: 'TYP-001',
  label: 'Geldig vraagtype',
  description: 'Het vraagtype moet geregistreerd zijn in het vraagtype-register (questionTypeRegistry.ts).',
  severity: 'BLOCKER',
  stage: 'type',
  category: 'Vraagtype',
  example: 'Geldige typen: multiple_choice, fill_in, ordering, matching, drag_drop_fill, table_reading, number_sequence, ratio_table, text_highlight, bar_chart, line_chart, pie_chart, schedule_reading, cijferend_optellen, cijferend_aftrekken, clock, money',
  fix: 'Gebruik een van de geregistreerde vraagtypes.'
}, {
  id: 'TYP-002',
  label: 'Antwoordformaat klopt bij type',
  description: 'Het antwoordobject moet overeenkomen met het verwachte formaat voor het vraagtype.',
  severity: 'BLOCKER',
  stage: 'type',
  category: 'Vraagtype',
  fix: 'Raadpleeg de vraagtype-specificatie voor het juiste antwoordformaat.'
}, {
  id: 'TYP-003',
  label: 'Multiple choice: 2–5 opties',
  description: 'Meerkeuze-vragen moeten minimaal 2 en maximaal 5 antwoordopties bevatten.',
  severity: 'WARNING',
  stage: 'type',
  category: 'Vraagtype',
  fix: 'Voeg meer antwoordopties toe of verander het vraagtype.'
},
// Content Stage
{
  id: 'CON-001',
  label: 'Vraagstelling niet leeg',
  description: 'De vraagstelling mag niet leeg zijn en moet minimaal 10 tekens bevatten.',
  severity: 'BLOCKER',
  stage: 'content',
  category: 'Inhoud',
  fix: 'Voeg een duidelijke vraagstelling toe.'
}, {
  id: 'CON-002',
  label: 'Uitleg aanwezig (explanationText)',
  description: 'feedback.explanationText is altijd verplicht. Minimale lengte afhankelijk van tier-band.',
  severity: 'BLOCKER',
  stage: 'content',
  category: 'Feedback',
  example: 'Minimale lengte: basic=30, intermediate=50, advanced=80, expert=100 tekens',
  fix: 'Voeg een stap-voor-stap uitleg toe in feedback.explanationText.'
}, {
  id: 'CON-003',
  label: 'Distractor-uitleg voor alle opties',
  description: 'feedback.distractorExplanations moet een uitleg bevatten voor elke antwoordoptie.',
  severity: 'BLOCKER',
  stage: 'content',
  category: 'Feedback',
  fix: 'Voeg een uitleg toe voor elke optie (index 0, 1, 2, 3).'
}, {
  id: 'CON-004',
  label: 'Geen verboden vocabulaire voor tier-band',
  description: 'Vragen op basic-niveau mogen geen technische termen bevatten zoals "notatie", "commutatief", "proportioneel".',
  severity: 'WARNING',
  stage: 'content',
  category: 'Taalgebruik',
  fix: 'Herformuleer de vraag met eenvoudiger taalgebruik.'
}, {
  id: 'CON-005',
  label: 'Vraaglengte binnen budget',
  description: 'De vraagstelling mag niet te lang zijn voor het tier-niveau.',
  severity: 'WARNING',
  stage: 'content',
  category: 'Taalgebruik',
  example: 'Max: basic=200, intermediate=400, advanced=600, expert=800 tekens',
  fix: 'Verkort de vraagstelling.'
},
// Support Stage
{
  id: 'SUP-001',
  label: 'Uitleg geeft antwoord niet weg',
  description: 'De uitleg mag het correcte antwoord niet direct vermelden zonder redenering.',
  severity: 'BLOCKER',
  stage: 'support',
  category: 'Ondersteuning',
  fix: 'Herschrijf de uitleg zodat deze de aanpak beschrijft, niet alleen het antwoord.'
}, {
  id: 'SUP-002',
  label: 'Hint aanwezig (verplichte families)',
  description: 'Families die hintsRequired=true hebben in het beleid moeten een hint bevatten.',
  severity: 'WARNING',
  stage: 'support',
  category: 'Ondersteuning',
  fix: 'Voeg een hint toe die de aanpak uitlegt zonder het antwoord te geven.'
},
// Visual Stage
{
  id: 'VIS-001',
  label: 'Stimulus visueel aanwezig (verplichte families)',
  description: 'Families met stimulusRequired=true moeten een stimulusAids-array hebben.',
  severity: 'BLOCKER',
  stage: 'visual',
  category: 'Visueel',
  example: 'Zie: src/governance/pipeline/defaultPolicy.ts → visual.familyRules',
  fix: 'Voeg een stimulusAids-array toe met het juiste visuele type.'
}, {
  id: 'VIS-002',
  label: 'Geen antwoordlek via visual',
  description: 'Stimulus-visuals mogen het correcte antwoord niet direct zichtbaar maken via label/value.',
  severity: 'WARNING',
  stage: 'visual',
  category: 'Visueel',
  fix: 'Pas het visuele hulpmiddel aan zodat het antwoord niet direct zichtbaar is.'
}, {
  id: 'VIS-003',
  label: 'stimulusAids/supportAids zijn arrays',
  description: 'Als aanwezig, moeten stimulusAids en supportAids arrays zijn (gebruik [] voor "beoordeeld, geen visuals nodig").',
  severity: 'BLOCKER',
  stage: 'visual',
  category: 'Visueel',
  fix: 'Verander het veld naar een array ([] of [{...}]).'
},
// Graph Stage
{
  id: 'GRA-001',
  label: 'Geen cyclische afhankelijkheden',
  description: 'De leerpad-graaf (LDG) mag geen cyclische afhankelijkheden bevatten.',
  severity: 'BLOCKER',
  stage: 'graph',
  category: 'LDG-Graaf',
  fix: 'Verwijder de cyclische afhankelijkheid uit de prerequisite-definitie.'
}, {
  id: 'GRA-002',
  label: 'FamilyId bestaat in de graaf',
  description: 'De familyId van een vraag moet geregistreerd zijn in de LDG-graaf.',
  severity: 'WARNING',
  stage: 'graph',
  category: 'LDG-Graaf',
  fix: 'Voeg de familyId toe aan de LDG-definitie in ldgBuilder.ts.'
},
// Policy
{
  id: 'POL-001',
  label: 'Minimaal aantal vragen per family',
  description: `Elke family moet minimaal ${DEFAULT_POLICY.gates.assessmentMinQuestionsPerFamily} vragen hebben voor assessment-pool.`,
  severity: 'WARNING',
  stage: 'policy',
  category: 'Beleid',
  fix: 'Voeg meer vragen toe aan de family.'
}, {
  id: 'POL-002',
  label: 'Kwaliteitsdrempel productie',
  description: `Vragen met score < ${DEFAULT_POLICY.gates.productionMinScore}% worden geblokkeerd voor productie.`,
  severity: 'BLOCKER',
  stage: 'policy',
  category: 'Beleid',
  fix: 'Verbeter de kwaliteit van de vraag.'
}, {
  id: 'POL-003',
  label: 'Kwaliteitsdrempel assessment',
  description: `Vragen met score < ${DEFAULT_POLICY.gates.assessmentMinScore}% zijn niet assessment-geschikt.`,
  severity: 'WARNING',
  stage: 'policy',
  category: 'Beleid',
  fix: 'Verbeter de kwaliteit voor assessment-gebruik.'
}];
// ─── Rulebook file registry ───────────────────────────────────────────────────
const RULEBOOK_FILES = [{
  id: 'master',
  name: 'Master Rulebook',
  path: 'governance/docs/MASTER_RULEBOOK.md',
  description: 'Canonieke regels voor ALLE modules — kwaliteit, structuur, feedback, distractors, tier-taal.',
  module: 'ALL',
  color: 'blue',
  keyRules: ['familyId, module, skill, subSkill, tier, levelCode, group zijn altijd verplicht', 'feedback.explanationText is altijd verplicht (2–6 regels, stap-voor-stap)', 'feedback.distractorExplanations voor ELKE optie verplicht', 'Distractors moeten realistische fouten/misconcepties vertegenwoordigen', 'Taalgebruik moet passen bij het tier-niveau (B6 = eenvoudig, E8+ = abstract)', 'Minimaal 1 "val-distractor" voor tiers ≥ E6']
}, {
  id: 'get',
  name: 'GET Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_GET.md',
  description: 'Aanvullende regels voor het Getalbegrip module (breuken, kommagetallen, procenten, etc.)',
  module: 'GET',
  color: 'violet',
  keyRules: ['LaTeX notatie verplicht voor breuken: $\\frac{1}{2}$', 'Komma als decimaalteken (niet punt): 3,14 niet 3.14', 'Visuele hulpmiddelen verplicht voor breuk-families (stimulusAids)', 'Machten en wortels in LaTeX: $2^3$, $\\sqrt{9}$']
}, {
  id: 'met',
  name: 'MET Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_MET.md',
  description: 'Aanvullende regels voor het Metriek module (meten, oppervlakte, inhoud, omtrek)',
  module: 'MET',
  color: 'teal',
  keyRules: ['Eenheden altijd vermelden: cm², m², km²', 'Formules expliciet noemen in uitleg (O = l × b)', 'Geo-visuals verplicht voor oppervlakte/omtrek families', 'Trap-distractor: eenheid verwisselen (cm vs. m)']
}, {
  id: 'tgl',
  name: 'TGL Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_TGL.md',
  description: 'Aanvullende regels voor het TijdGeld module (klok, kalender, euro, wisselgeld)',
  module: 'TGL',
  color: 'amber',
  keyRules: ['Klok-visuals verplicht voor analoge tijdvragen', 'Euro-notatie: €12,50 (komma, niet punt)', 'Tijdsduur altijd in uren EN minuten vermelden', 'Trap-distractor: uren/minuten verwisselen']
}, {
  id: 'sww',
  name: 'SWW Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_SWW.json',
  description: 'Aanvullende regels voor Spelling Werkwoorden (stam, dt-regel, kofschip, etc.)',
  module: 'SWW',
  color: 'rose',
  keyRules: ['Kofschip-regel altijd vermelden in uitleg', 'Stam-berekening stap voor stap uitleggen', 'Trap-distractor: dt-fout (werkt/werkt)', 'Hints verplicht voor alle SWW-families']
}, {
  id: 'spr',
  name: 'SPR Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_SPR.md',
  description: 'Aanvullende regels voor Spelling Regels (open/gesloten lettergreep, trema, etc.)',
  module: 'SPR',
  color: 'pink',
  keyRules: ['Lettergreep-analyse altijd tonen in uitleg', 'Trema-regel expliciet vermelden', 'Trap-distractor: vergelijkbaar klinkend woord']
}, {
  id: 'taa',
  name: 'TAA Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_TAA.md',
  description: 'Aanvullende regels voor Taalbegrip (lezen, zinsbouw, woordsoorten)',
  module: 'TAA',
  color: 'emerald',
  keyRules: ['Tekst-context altijd meegeven bij leesvragen', 'Woordsoort-definitie vermelden in uitleg', 'Trap-distractor: vergelijkbare maar foute woordsoort']
}, {
  id: 'topo',
  name: 'TOPO Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_TOPO.md',
  description: 'Aanvullende regels voor Topografie (provincies, landen, rivieren, etc.)',
  module: 'TOPO',
  color: 'cyan',
  keyRules: ['Kaart-visual verplicht voor locatievragen', 'Trap-distractor: aangrenzend land/provincie', 'Hoofdstad vs. grootste stad onderscheid vermelden']
}, {
  id: 'stu',
  name: 'STU Rulebook',
  path: 'governance/docs/ADDON_RULEBOOK_STU.md',
  description: 'Aanvullende regels voor Studievaardigheden (tabellen, grafieken, kaarten)',
  module: 'STU',
  color: 'indigo',
  keyRules: ['Tabel/grafiek-visual altijd verplicht als stimulus', 'Vraag verwijst altijd naar specifieke cel/punt in de visual', 'Trap-distractor: naburige cel/waarde in de tabel']
}];
// ─── Pipeline source files ────────────────────────────────────────────────────
const PIPELINE_FILES = [{
  name: 'Schema Stage',
  path: 'src/governance/pipeline/stages/schemaStage.ts',
  stage: 'schema' as Stage,
  description: 'Valideert JSON-structuur, verplichte velden, ID-formaat en tier-bereik.'
}, {
  name: 'Type Stage',
  path: 'src/governance/pipeline/stages/typeStage.ts',
  stage: 'type' as Stage,
  description: 'Valideert vraagtype-registratie en antwoordformaat per type.'
}, {
  name: 'Content Stage',
  path: 'src/governance/pipeline/stages/contentStage.ts',
  stage: 'content' as Stage,
  description: 'Valideert uitleg, distractors, taalgebruik en lengte-budgetten.'
}, {
  name: 'Support Stage',
  path: 'src/governance/pipeline/stages/supportStage.ts',
  stage: 'support' as Stage,
  description: 'Valideert aanwezigheid van hints en uitleg voor verplichte families.'
}, {
  name: 'Visual Stage',
  path: 'src/governance/pipeline/stages/visualStage.ts',
  stage: 'visual' as Stage,
  description: 'Valideert visuele hulpmiddelen, antwoordlekken en array-conventie.'
}, {
  name: 'Graph Stage',
  path: 'src/governance/pipeline/stages/graphStage.ts',
  stage: 'graph' as Stage,
  description: 'Valideert LDG-graaf, DAG-integriteit en cyclische afhankelijkheden.'
}, {
  name: 'Default Policy',
  path: 'src/governance/pipeline/defaultPolicy.ts',
  stage: 'policy' as Stage,
  description: 'Alle drempelwaarden, vocabulaire-lijsten, visuele regels en feature flags.'
}, {
  name: 'Issue Contract',
  path: 'src/governance/pipeline/issueContract.ts',
  stage: 'policy' as Stage,
  description: 'Canoniek issue-type (ValidationIssue), gate-logica en drempelwaarden.'
}];
// ─── Shared config ────────────────────────────────────────────────────────────
const STAGE_LABELS: Record<Stage, string> = {
  schema: 'Schema',
  type: 'Vraagtype',
  content: 'Inhoud',
  support: 'Ondersteuning',
  visual: 'Visueel',
  graph: 'LDG-Graaf',
  policy: 'Beleid'
};
const STAGE_COLORS: Record<Stage, string> = {
  schema: 'bg-slate-100 text-slate-700',
  type: 'bg-blue-100 text-blue-700',
  content: 'bg-violet-100 text-violet-700',
  support: 'bg-teal-100 text-teal-700',
  visual: 'bg-orange-100 text-orange-700',
  graph: 'bg-pink-100 text-pink-700',
  policy: 'bg-amber-100 text-amber-700'
};
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-3597" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Blokkerend'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-3598" />,
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    label: 'Waarschuwing'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-3599" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Informatief'
  }
};
// ─── Sub-components ───────────────────────────────────────────────────────────
function RuleCard({
  rule
}: {
  rule: Rule;
}) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[rule.severity];
  return <div className={`rounded-lg border ${sev.border} bg-white overflow-hidden`} data-id="element-3600">
      <button className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-3601">
        <div className={`mt-0.5 flex-shrink-0 ${sev.text}`} data-id="element-3602">{sev.icon}</div>
        <div className="min-w-0 flex-1" data-id="element-3603">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3604">
            <code className="text-xs font-mono text-gray-400" data-id="element-3605">{rule.id}</code>
            <span className="font-semibold text-gray-800" data-id="element-3606">{rule.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[rule.stage]}`} data-id="element-3607">
              {STAGE_LABELS[rule.stage]}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sev.bg} ${sev.text}`} data-id="element-3608">
              {sev.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600" data-id="element-3609">{rule.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-3610">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3611" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3612" />}
        </div>
      </button>
      {expanded && <div className={`border-t ${sev.border} ${sev.bg} px-4 py-3 space-y-3`} data-id="element-3613">
          {rule.example && <div data-id="element-3614">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3615">
                Bron / Voorbeeld
              </p>
              <code className="block rounded bg-white/70 px-3 py-2 text-xs text-gray-700 border border-gray-200" data-id="element-3616">
                {rule.example}
              </code>
            </div>}
          {rule.fix && <div data-id="element-3617">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3618">
                Oplossing
              </p>
              <p className="text-sm text-gray-700" data-id="element-3619">{rule.fix}</p>
            </div>}
        </div>}
    </div>;
}
function RulebookCard({
  book
}: {
  book: (typeof RULEBOOK_FILES)[0];
}) {
  const [expanded, setExpanded] = useState(false);
  const colorMap: Record<string, {
    badge: string;
    border: string;
    bg: string;
  }> = {
    blue: {
      badge: 'bg-blue-100 text-blue-700',
      border: 'border-blue-200',
      bg: 'bg-blue-50'
    },
    violet: {
      badge: 'bg-violet-100 text-violet-700',
      border: 'border-violet-200',
      bg: 'bg-violet-50'
    },
    teal: {
      badge: 'bg-teal-100 text-teal-700',
      border: 'border-teal-200',
      bg: 'bg-teal-50'
    },
    amber: {
      badge: 'bg-amber-100 text-amber-700',
      border: 'border-amber-200',
      bg: 'bg-amber-50'
    },
    rose: {
      badge: 'bg-rose-100 text-rose-700',
      border: 'border-rose-200',
      bg: 'bg-rose-50'
    },
    pink: {
      badge: 'bg-pink-100 text-pink-700',
      border: 'border-pink-200',
      bg: 'bg-pink-50'
    },
    emerald: {
      badge: 'bg-emerald-100 text-emerald-700',
      border: 'border-emerald-200',
      bg: 'bg-emerald-50'
    },
    cyan: {
      badge: 'bg-cyan-100 text-cyan-700',
      border: 'border-cyan-200',
      bg: 'bg-cyan-50'
    },
    indigo: {
      badge: 'bg-indigo-100 text-indigo-700',
      border: 'border-indigo-200',
      bg: 'bg-indigo-50'
    }
  };
  const c = colorMap[book.color] ?? colorMap.blue;
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-3620">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} data-id="element-3621">
        <FileTextIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-3622" />
        <div className="min-w-0 flex-1" data-id="element-3623">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-3624">
            <span className="font-semibold text-gray-800" data-id="element-3625">{book.name}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-3626">
              {book.module}
            </span>
          </div>
          <code className="text-xs text-gray-400 font-mono" data-id="element-3627">{book.path}</code>
          <p className="text-sm text-gray-600 mt-1" data-id="element-3628">{book.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-3629">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3630" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3631" />}
        </div>
      </button>
      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3`} data-id="element-3632">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2" data-id="element-3633">
            Kernregels
          </p>
          <ul className="space-y-1.5" data-id="element-3634">
            {book.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-3635">
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-3636" />
                {rule}
              </li>)}
          </ul>
        </div>}
    </div>;
}
function ThresholdSection() {
  const gates = DEFAULT_POLICY.gates;
  const budgets = DEFAULT_POLICY.contentBudgets;
  const flags = DEFAULT_POLICY.featureFlags;
  const thresholdItems = [{
    label: 'Productie min. score',
    value: `${gates.productionMinScore}%`,
    description: 'Vragen onder deze score worden geblokkeerd voor productie (oefenmodus).',
    path: 'src/governance/pipeline/defaultPolicy.ts → gates.productionMinScore',
    color: gates.productionMinScore >= 70 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Assessment min. score',
    value: `${gates.assessmentMinScore}%`,
    description: 'Vragen onder deze score zijn niet geschikt voor CITO-stijl toetsen.',
    path: 'src/governance/pipeline/defaultPolicy.ts → gates.assessmentMinScore',
    color: gates.assessmentMinScore >= 80 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Review drempel (coach ernst)',
    value: `≥ ${gates.reviewCoachSeverityThreshold}`,
    description: 'Coach-issues met ernst ≥ deze waarde triggeren een review-markering.',
    path: 'src/governance/pipeline/defaultPolicy.ts → gates.reviewCoachSeverityThreshold',
    color: 'text-blue-700'
  }, {
    label: 'Min. vragen per family (assessment)',
    value: `${gates.assessmentMinQuestionsPerFamily}`,
    description: 'Families met minder vragen zijn niet assessment-geschikt.',
    path: 'src/governance/pipeline/defaultPolicy.ts → gates.assessmentMinQuestionsPerFamily',
    color: 'text-blue-700'
  }];
  const budgetItems = [{
    label: 'Min. uitleglengte (basic)',
    value: `${budgets.minExplanationLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4 (B6/M6)',
    path: 'contentBudgets.minExplanationLengthByTierBand.basic'
  }, {
    label: 'Min. uitleglengte (intermediate)',
    value: `${budgets.minExplanationLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8 (E6/B7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.intermediate'
  }, {
    label: 'Min. uitleglengte (advanced)',
    value: `${budgets.minExplanationLengthByTierBand.advanced} tekens`,
    description: 'Tiers 9–12 (M7/E7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.advanced'
  }, {
    label: 'Min. uitleglengte (expert)',
    value: `${budgets.minExplanationLengthByTierBand.expert} tekens`,
    description: 'Tiers 13–16 (B8/E8+)',
    path: 'contentBudgets.minExplanationLengthByTierBand.expert'
  }, {
    label: 'Min. distractor-uitleg (basic)',
    value: `${budgets.minDistractorLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4',
    path: 'contentBudgets.minDistractorLengthByTierBand.basic'
  }, {
    label: 'Min. distractor-uitleg (intermediate)',
    value: `${budgets.minDistractorLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8',
    path: 'contentBudgets.minDistractorLengthByTierBand.intermediate'
  }];
  const featureFlagItems = Object.entries(flags).map(([key, value]) => ({
    key,
    value: value as boolean,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  }));
  return <div className="space-y-6" data-id="element-3637">
      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-3638">
        <div className="flex items-start gap-3" data-id="element-3639">
          <InfoIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3640" />
          <div data-id="element-3641">
            <p className="text-sm font-semibold text-blue-800" data-id="element-3642">
              Drempelwaarden aanpassen
            </p>
            <p className="text-sm text-blue-700 mt-0.5" data-id="element-3643">
              Drempelwaarden zijn gedefinieerd in{' '}
              <code className="font-mono bg-blue-100 px-1 rounded" data-id="element-3644">
                src/governance/pipeline/defaultPolicy.ts
              </code>
              . Pas het bestand direct aan om drempelwaarden te wijzigen.
              Gebruik de <strong data-id="element-3645">Instellingen</strong> tab voor
              runtime-aanpassingen.
            </p>
          </div>
        </div>
      </div>

      {/* Gate Thresholds */}
      <div data-id="element-3646">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3647">
          Kwaliteitspoorten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3648">
          {thresholdItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < thresholdItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3649">
              <div className="min-w-0" data-id="element-3650">
                <p className="text-sm font-medium text-gray-800" data-id="element-3651">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3652">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3653">
                  {item.path}
                </code>
              </div>
              <span className={`text-lg font-bold flex-shrink-0 ${item.color}`} data-id="element-3654">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Content Budgets */}
      <div data-id="element-3655">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3656">
          Inhoudsbudgetten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3657">
          {budgetItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < budgetItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3658">
              <div className="min-w-0" data-id="element-3659">
                <p className="text-sm font-medium text-gray-800" data-id="element-3660">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3661">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3662">
                  {item.path}
                </code>
              </div>
              <span className="text-sm font-bold text-gray-700 flex-shrink-0" data-id="element-3663">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Feature Flags */}
      <div data-id="element-3664">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3665">
          Feature Flags
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3666">
          {featureFlagItems.map((item, i) => <div key={item.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < featureFlagItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3667">
              <div className="min-w-0" data-id="element-3668">
                <p className="text-sm font-medium text-gray-800" data-id="element-3669">
                  {item.label}
                </p>
                <code className="text-xs text-gray-400 font-mono" data-id="element-3670">
                  featureFlags.{item.key}
                </code>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0 ${item.value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3671">
                {item.value ? 'Actief' : 'Inactief'}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}
// ─── Main component ───────────────────────────────────────────────────────────
export function GovernanceRulesTab() {
  const [view, setView] = useState<ViewId>('regels');
  const [search, setSearch] = useState('');
  const [selectedStage, setSelectedStage] = useState<Stage | 'all'>('all');
  const [selectedSeverity, setSelectedSeverity] = useState<Severity | 'all'>('all');
  const filtered = RULES.filter(rule => {
    const matchesSearch = search === '' || rule.label.toLowerCase().includes(search.toLowerCase()) || rule.description.toLowerCase().includes(search.toLowerCase()) || rule.id.toLowerCase().includes(search.toLowerCase());
    const matchesStage = selectedStage === 'all' || rule.stage === selectedStage;
    const matchesSeverity = selectedSeverity === 'all' || rule.severity === selectedSeverity;
    return matchesSearch && matchesStage && matchesSeverity;
  });
  const blockers = RULES.filter(r => r.severity === 'BLOCKER').length;
  const warnings = RULES.filter(r => r.severity === 'WARNING').length;
  const infos = RULES.filter(r => r.severity === 'INFO').length;
  const stages: (Stage | 'all')[] = ['all', 'schema', 'type', 'content', 'support', 'visual', 'graph', 'policy'];
  const views: {
    id: ViewId;
    label: string;
    icon: React.ReactNode;
  }[] = [{
    id: 'regels',
    label: 'Validatieregels',
    icon: <ShieldCheckIcon className="h-4 w-4" data-id="element-3672" />
  }, {
    id: 'regelboeken',
    label: 'Regelboeken',
    icon: <FolderIcon className="h-4 w-4" data-id="element-3673" />
  }, {
    id: 'drempelwaarden',
    label: 'Drempelwaarden',
    icon: <SlidersIcon className="h-4 w-4" data-id="element-3674" />
  }];
  return <div className="space-y-5" data-id="element-3675">
      {/* View Switcher */}
      <div className="flex gap-1 rounded-xl border border-gray-100 bg-white p-1" data-id="element-3676">
        {views.map(v => <button key={v.id} onClick={() => setView(v.id)} className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${view === v.id ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'}`} data-id="element-3677">
            {v.icon}
            <span className="hidden sm:inline" data-id="element-3678">{v.label}</span>
          </button>)}
      </div>

      {/* ── View: Validatieregels ── */}
      {view === 'regels' && <>
          {/* Summary */}
          <div className="grid grid-cols-3 gap-3" data-id="element-3679">
            <div className="rounded-lg border border-red-100 bg-red-50 p-3 text-center" data-id="element-3680">
              <p className="text-2xl font-bold text-red-700" data-id="element-3681">{blockers}</p>
              <p className="text-xs font-medium text-red-600" data-id="element-3682">
                Blokkerende Regels
              </p>
            </div>
            <div className="rounded-lg border border-amber-100 bg-amber-50 p-3 text-center" data-id="element-3683">
              <p className="text-2xl font-bold text-amber-700" data-id="element-3684">{warnings}</p>
              <p className="text-xs font-medium text-amber-600" data-id="element-3685">
                Waarschuwingsregels
              </p>
            </div>
            <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-center" data-id="element-3686">
              <p className="text-2xl font-bold text-blue-700" data-id="element-3687">{infos}</p>
              <p className="text-xs font-medium text-blue-600" data-id="element-3688">
                Informatieve Regels
              </p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex flex-col gap-3 sm:flex-row" data-id="element-3689">
            <div className="relative flex-1" data-id="element-3690">
              <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-3691" />
              <input type="text" placeholder="Zoek regels op naam, beschrijving of ID..." value={search} onChange={e => setSearch(e.target.value)} className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-4 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" data-id="element-3692" />
            </div>
            <select value={selectedSeverity} onChange={e => setSelectedSeverity(e.target.value as Severity | 'all')} className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" data-id="element-3693">
              <option value="all" data-id="element-3694">Alle ernst</option>
              <option value="BLOCKER" data-id="element-3695">Blokkerend</option>
              <option value="WARNING" data-id="element-3696">Waarschuwing</option>
              <option value="INFO" data-id="element-3697">Informatief</option>
            </select>
          </div>

          {/* Stage Filter Pills */}
          <div className="flex flex-wrap gap-2" data-id="element-3698">
            <FilterIcon className="h-4 w-4 text-gray-400 self-center" data-id="element-3699" />
            {stages.map(stage => <button key={stage} onClick={() => setSelectedStage(stage)} className={`rounded-full px-3 py-1 text-xs font-medium transition-all ${selectedStage === stage ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3700">
                {stage === 'all' ? 'Alle fasen' : STAGE_LABELS[stage as Stage]}
              </button>)}
          </div>

          <p className="text-sm text-gray-500" data-id="element-3701">
            {filtered.length} van {RULES.length} regels weergegeven
          </p>

          <div className="space-y-2" data-id="element-3702">
            {filtered.length === 0 ? <div className="rounded-lg border border-gray-100 bg-gray-50 py-12 text-center" data-id="element-3703">
                <SearchIcon className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-3704" />
                <p className="text-gray-500" data-id="element-3705">
                  Geen regels gevonden voor deze zoekopdracht.
                </p>
              </div> : filtered.map(rule => <RuleCard key={rule.id} rule={rule} data-id="element-3706" />)}
          </div>
        </>}

      {/* ── View: Regelboeken ── */}
      {view === 'regelboeken' && <div className="space-y-5" data-id="element-3707">
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-4" data-id="element-3708">
            <p className="text-sm text-gray-600" data-id="element-3709">
              De governance-regels zijn verdeeld over{' '}
              <strong data-id="element-3710">regelboek-bestanden</strong> (markdown) en{' '}
              <strong data-id="element-3711">pipeline-bronbestanden</strong> (TypeScript). Klik op een
              kaart om de kernregels te bekijken.
            </p>
          </div>

          {/* Rulebook files */}
          <div data-id="element-3712">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-3713">
              <FileTextIcon className="h-4 w-4" data-id="element-3714" />
              Regelboek Bestanden (Markdown)
            </h3>
            <div className="space-y-2" data-id="element-3715">
              {RULEBOOK_FILES.map(book => <RulebookCard key={book.id} book={book} data-id="element-3716" />)}
            </div>
          </div>

          {/* Pipeline source files */}
          <div data-id="element-3717">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2" data-id="element-3718">
              <CodeIcon className="h-4 w-4" data-id="element-3719" />
              Pipeline Bronbestanden (TypeScript)
            </h3>
            <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3720">
              {PIPELINE_FILES.map((file, i) => <div key={file.path} className={`flex items-start gap-3 px-4 py-3 ${i < PIPELINE_FILES.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3721">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium flex-shrink-0 mt-0.5 ${STAGE_COLORS[file.stage]}`} data-id="element-3722">
                    {STAGE_LABELS[file.stage]}
                  </span>
                  <div className="min-w-0 flex-1" data-id="element-3723">
                    <p className="text-sm font-semibold text-gray-800" data-id="element-3724">
                      {file.name}
                    </p>
                    <code className="text-xs text-gray-400 font-mono" data-id="element-3725">
                      {file.path}
                    </code>
                    <p className="text-xs text-gray-500 mt-0.5" data-id="element-3726">
                      {file.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>}

      {/* ── View: Drempelwaarden ── */}
      {view === 'drempelwaarden' && <ThresholdSection data-id="element-3727" />}
    </div>;
}